import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { useNavigate } from 'react-router-dom';
import {adminGetCompanies, updateAPIReportData, getUserDetails, getAPIData} from '../../api/api'
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { DataGrid, Column, Paging, } from 'devextreme-react/data-grid';
import { SelectBox } from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react/tooltip';
import { groupBy } from '../../utils/miscUtil';
import {sifTimesOfEvent, iogpValues, yesnoValues, causalValues, sifStatusValues} from '../../api/api'
import './sif.scss';

var basins = require('../../api/Basins.json');
basins = basins.features.map((el) => (el.properties.BASIN_NAME))

var states = require('../../api/States.json');
states.sort((a,b) => (a.State > b.State) ? 1 : ((b.State > a.State) ? -1 : 0))
states = groupBy(states, 'Abbr')

var counties = require('../../api/Counties.json');
counties = groupBy(counties, 'State')

var timesOfEvent = sifTimesOfEvent.map((el) => (el.value))
var iogpVL = iogpValues.map((el) => (el.value))
var yesnoVL = yesnoValues.map((el) => (el.value))
var causalVL = causalValues.map((el) => (el.value))
var sifStatusVL = sifStatusValues.map((el) => (el.value))

var disciplines = require('../../api/DiscipineTree.json');
disciplines = groupBy(disciplines, 'Discipline')

var categories = require('../../api/CategoryTree.json');
categories = groupBy(categories, 'Category');

export default function SIFUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef();
  const { user, refreshSession } = useAuth();
  const [originalData, setOriginalData] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [companiesList, setCompaniesList] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const onGetCompaniesSuccess = useCallback((data) => {
    data = data.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0))
    setCompaniesList(data);
    setLoading(false);
  }, [setCompaniesList, setLoading])

  const onError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      setLoading(false);
    }
  }, [refreshSession]);

  const onUserDetailsSuccess = useCallback((data) => {
    setCurrentCompany(data);
    setLoading(false);
  }, [setCurrentCompany])

  const onGetDataSuccess = useCallback((data) => {
    setExistingData(data);
  }, [setExistingData]);

  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
            adminGetCompanies(user, onGetCompaniesSuccess, onError);
            break;
          }
          if (groups[i].includes('sifrw')) {
            has_perm=true;
            getUserDetails(user, onUserDetailsSuccess, onError);
            break;
          }
        }
      }
      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }
      getAPIData("/reports/data/sif", user, onGetDataSuccess, onError);
    }
  }, [user, navigate, onError, onGetCompaniesSuccess, onUserDetailsSuccess, onGetDataSuccess]);

  async function handleFileChange(event) {
    const file = event.target.files[0];
    try {
      const result = await csvToArray(file);
      setOriginalData(result.array);
    } catch (error) {
      notify(error, 'error', 3000);
      console.error(error);
    }
  };

  const readClipboard = async () => {
    var lncnt = 0;
    try {
      const text = await navigator.clipboard.readText();
      var line_delimiter = '\n';
      if (text.includes('\r\n')) {
        line_delimiter = '\r\n';
        console.log("line delimiter is rn");
      } else {
        console.log("line delimiter is n");
      }
      const lines = text.split(line_delimiter);
      const array = [];
      const first_line = [];
      for (const line of lines) {
        lncnt=lncnt+1;
        const values = line.split('\t');

        if (values[1] === "") {
          continue;
        }
        if (first_line.length === 0) {
          if (values[0] !== "Company") {
            continue;
          }
          first_line.push(...values);
          continue;
        }
        var el = {}
        for (var i=0; i<values.length; i++){
          el[first_line[i].replace("\r","")]=values[i].replace("\r","")
        }
        //Handle microsoft BS
        if (values.length===1) {
          if (values[0]==='') {
            continue
          }
        }
        console.log(values);
        var currentData = groupBy(existingData, 'company_id')
        validateSIFEvent(el, currentCompany, currentData[currentCompany['company_id']]);
        array.push(el);
      }
      let dupdate = groupBy(array, 'DATE OF EVENT (m/d/yyyy)')
      for (var d in dupdate) {
        if (dupdate[d].length > 1) {
          let dupdesc = groupBy(dupdate[d], 'DESCRIPTION OF INCIDENT')
          for (var desc in dupdesc) {
            if (dupdesc[desc].length > 1) {
              console.log("duplicate: ", dupdesc[desc]);
              for (i=1; i<dupdesc[desc].length; i++) {
                dupdesc[desc][i]['is_duplicate_error'] = true;
                dupdesc[desc][i]['invalid_fields'].push("DATE OF EVENT (m/d/yyyy)");
                dupdesc[desc][i]['invalid_fields'].push('DESCRIPTION OF INCIDENT');
                dupdesc[desc][i]['error_message'] = "Duplicate event."
              }
            }
          }
        }
      }
      setOriginalData(array);
    } catch (err) {
      notify("Failed to read clipboard contents. Error at row: "+lncnt.toString(), 'error', 2000);
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const csvToArray = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (event) {
        const csv = event.target.result;
        const lines = csv.split('\n');
        const array = [];
        const first_line = [];
        const skipped_lines = [];
        for (const line of lines) {
          const values = line.split('\t');
          if (values[1] === "") {
            continue;
          }
          if (first_line.length === 0) {
            if (values[0] !== "Company") {
              continue;
            }
            first_line.push(...values);
            continue;
          }
          var el = {}
          for (var i=0; i<values.length; i++){
            el[first_line[i].replace("\r","")]=values[i].replace("\r","")
          }
          validateSIFEvent(el, currentCompany, existingData);
          array.push(el);
        }
        resolve({array:array, skipped_lines:skipped_lines});
      };

      reader.onerror = function () {
        reject(new Error('Error reading the file.'));
      };

      reader.readAsText(file);
    });
  }, [currentCompany])

  const validateSIFEvent = (e, currentCompany, currentData) => {
    var invalid_fields=[];
    var has_validation_warning=false;
    var has_validation_error=false;
    var is_duplicate_error=false;
    var error_message="";
    e["company_id"] = currentCompany.company_id
    if (!e['Company']) {
      e["Company"] = currentCompany.company_name
    }
    if (isNaN(Date.parse(e['DATE OF EVENT (m/d/yyyy)']))) {
      invalid_fields.push("DATE OF EVENT (m/d/yyyy)")
      has_validation_error=true;
      //e['DATE OF EVENT (m/d/yyyy)'] = "Invalid Date"
      error_message = "Invalid Date."
    } else {
      e['DATE OF EVENT (m/d/yyyy)'] = new Date(e['DATE OF EVENT (m/d/yyyy)'])
      if(currentData) {
        for (var i=0; i<currentData.length; i++) {
          if ((new Date(currentData[i]['DATE OF EVENT (m/d/yyyy)'])).getTime()===e['DATE OF EVENT (m/d/yyyy)'].getTime()) {
            if (e['DESCRIPTION OF INCIDENT']===currentData[i]['DESCRIPTION OF INCIDENT']) {
              is_duplicate_error = true;
              invalid_fields.push("DATE OF EVENT (m/d/yyyy)");
              invalid_fields.push('DESCRIPTION OF INCIDENT');
              error_message = "Duplicate event."
            }
          }
        }
      }
    }
    if (e['Area/Basin'] && (!basins.includes(e['Area/Basin']))) {
      invalid_fields.push("Area/Basin")
      has_validation_warning=true;
      error_message = "Invalid Area/Basin. Please select one of the following values: "+((basins.toString()).replaceAll(",",", "));
    }
    if (e['State']) {
      if (!(e['State'] in states)) {
        invalid_fields.push("State")
        has_validation_warning=true;
        error_message = "Invalid State. Please select one of the following values: "+((Object.keys(states).toString()).replaceAll(",",", "));
      } else {
        var st = states[e['State']][0]["State"]
        var cl = counties[st]
        cl = cl.map((el) => (el.County))
        if (e['County'] && (!cl.includes(e['County']))) {
          invalid_fields.push("County")
          has_validation_warning=true;
          error_message = "Invalid County. Please select one of the following values: "+(cl.toString().replaceAll(",",", "));
        }
      }
    } else {
      if (e['County']) {
        invalid_fields.push("County")
        has_validation_warning=true;
        error_message = "Invalid County. Please select a state first.";
      }
    }
    if (e['Time of Event'] && (!timesOfEvent.includes(e['Time of Event']))) {
      invalid_fields.push("Time of Event")
      has_validation_warning=true;
      error_message = "Invalid Time of Event. Please select one of the following values: "+(timesOfEvent.toString().replaceAll(",",", "));
    }

    if (e['Type'].length === 0) {
      invalid_fields.push("Type")
      has_validation_error=true;
      error_message = "Type is mandatory.";
    } else {
      if (["Actual", "Potential"].includes(e['Type'])) {
        let fat = parseInt(e['Number of Fatalities']) || 0;
        let inj = parseInt(e['Number of Serious Injuries']) || 0;
        if (e['Type']==="Actual") {
          if (fat+inj===0){
            invalid_fields.push("Type");
            invalid_fields.push("Number of Fatalities");
            invalid_fields.push("Number of Serious Injuries");
            has_validation_error=true;
            error_message = "Actual events must have at least on injury or fatality.";
          }
        } else {
          if (fat+inj>0){
            invalid_fields.push("Type");
            invalid_fields.push("Number of Fatalities");
            invalid_fields.push("Number of Serious Injuries");
            has_validation_error=true;
            error_message = "Potential events must have both injury or fatality numbers 0.";
          }
        }
      } else {
        invalid_fields.push("Type");
        has_validation_error=true;
        error_message = "Invalid Type. Please select one of the following values: "+(["Actual", "Potential"].toString().replaceAll(",",", "));
      }

    }

    if (e['Number of Fatalities'].length === 0) {
      invalid_fields.push("Number of Fatalities")
      has_validation_error=true;
      e['Number of Fatalities'] = "Invalid Number"
      error_message = "Number of Fatalities must be a number";
    } else {
      if (parseInt(e['Number of Fatalities']).toString() !== e['Number of Fatalities']) {
        invalid_fields.push("Number of Fatalities")
        has_validation_error=true;
        e['Number of Fatalities'] = "Invalid Number"
        error_message = "Number of Fatalities must be a number";
      }
    }

    if (e['Number of Serious Injuries'].length === 0) {
      invalid_fields.push("Number of Serious Injuries")
      has_validation_error=true;
      e['Number of Serious Injuries'] = "Invalid Number"
      error_message = "Number of Serious Injuries must be a number";
    } else {
      if (parseInt(e['Number of Serious Injuries']).toString() !== e['Number of Serious Injuries']) {
        invalid_fields.push("Number of Serious Injuries")
        has_validation_error=true;
        e['Number of Serious Injuries'] = "Invalid Number"
        error_message = "Number of Serious Injuries must be a number";
      }
    }

    if (e['DESCRIPTION OF INCIDENT'].length < 10) {
      invalid_fields.push("DESCRIPTION OF INCIDENT")
      has_validation_error=true;
      error_message = "Description has to be at least 10 characters long.";
    }

    if (e['SIF CATEGORY'].length === 0) {
      invalid_fields.push("SIF CATEGORY")
      has_validation_error=true;
      e['SIF CATEGORY'] = "Invalid Value"
      if (e['SIF Condition']) {
        invalid_fields.push("SIF Condition")
        has_validation_warning=true;
      }
      error_message = "Category is mandatory. Please select one of the following values: "+(Object.keys(categories).toString().replaceAll(",",", "));
    } else {
      if (!Object.keys(categories).includes(e['SIF CATEGORY'])) {
        invalid_fields.push("SIF CATEGORY")
        has_validation_error=true;
        error_message = "Invalid Category. Please select one of the following values: "+(Object.keys(categories).toString().replaceAll(",",", "));
      } else {
        let condList = categories[e['SIF CATEGORY']].map((el) => (el.Condition))
        if (e['SIF Condition'] && (!condList.includes(e['SIF Condition']))) {
          invalid_fields.push("SIF Condition")
          has_validation_warning=true;
          error_message = "Invalid Condition. Please select one of the following values: "+(condList.toString().replaceAll(",",", "));
        }
      }
    }

    if (e['Discipline of Activity at Time of Event']) {
      if (!Object.keys(disciplines).includes(e['Discipline of Activity at Time of Event'])) {
        invalid_fields.push("Discipline of Activity at Time of Event")
        has_validation_warning=true;
        error_message = "Invalid Discipline of Activity. Please select one of the following values: "+(disciplines.toString().replaceAll(",",", "));
      } else {
        let actList = groupBy(disciplines[e["Discipline of Activity at Time of Event"]], 'Activity')
        if (e['Activity at Time of Event']) {
          if (!Object.keys(actList).includes(e['Activity at Time of Event'])) {
            invalid_fields.push("Activity at Time of Event")
            has_validation_warning=true;
            error_message = "Invalid Activity. Please select one of the following values: "+(Object.keys(actList).toString().replaceAll(",",", "));
          } else {
            let subactList = actList[e["Activity at Time of Event"]].map((el) => (el.Subactivity))
            if (e['Activity Subgroup or Task'] && (!subactList.includes(e['Activity Subgroup or Task']))) {
              invalid_fields.push("Activity Subgroup or Task")
              has_validation_warning=true;
              error_message = "Invalid Activity Subgroup or Task. Please select one of the following values: "+(subactList.toString().replaceAll(",",", "));
            }
          }
        } else {
          if (e['Activity Subgroup or Task']) {
            invalid_fields.push("Activity Subgroup or Task")
            has_validation_warning=true;
            error_message = "Missing Activity. Please select an activity first.";
          }
        }
      }
    } else {
      if (e['Activity at Time of Event']) {
        invalid_fields.push("Activity at Time of Event")
        has_validation_warning=true;
        error_message = "Missing Discipline of Activity. Please select a Discipline of Activity first.";
      }
      if (e['Activity Subgroup or Task']) {
        invalid_fields.push("Activity Subgroup or Task")
        has_validation_warning=true;
        error_message = "Missing Discipline of Activity. Please select a Discipline of Activity first.";
      }
    }

    if (e['IOGP Life-Saving Rule Violation'] && (!iogpVL.includes(e['IOGP Life-Saving Rule Violation']))) {
      invalid_fields.push("IOGP Life-Saving Rule Violation")
      has_validation_warning=true;
      error_message = "Invalid IOGP Life-Saving Rule Violation. Please select one of the following values: "+(iogpVL.toString().replaceAll(",",", "));
    }

    if (e['Was it reasonable that a person was or could have been in proximity at the time of the event?'] && (!yesnoVL.includes(e['Was it reasonable that a person was or could have been in proximity at the time of the event?']))) {
      invalid_fields.push("Was it reasonable that a person was or could have been in proximity at the time of the event?")
      has_validation_warning=true;
      error_message = "Invalid value for Was it reasonable that a person was or could have been in proximity at the time of the event? Please select one of the following values: "+(yesnoVL.toString().replaceAll(",",", "));
    }

    if (e['Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'] && (!yesnoVL.includes(e['Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?']))) {
      invalid_fields.push("Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?")
      has_validation_warning=true;
      error_message = "Invalid value for Did a barrier/control prevent or mitigate the event from becoming an Actual SIF? Please select one of the following values: "+(yesnoVL.toString().replaceAll(",",", "));
    }

    if (e['Primary Causal Factors'] && (!causalVL.includes(e['Primary Causal Factors']))) {
      invalid_fields.push("Primary Causal Factors")
      has_validation_warning=true;
      error_message = "Invalid Primary Causal Factor. Please select one of the following values: "+(causalVL.toString().replaceAll(",",", "));
    }
    if (e['What is the status of the Causal Investigation?']) {
      if (!sifStatusVL.includes(e['What is the status of the Causal Investigation?'])) {
        invalid_fields.push("What is the status of the Causal Investigation?")
        has_validation_warning=true;
        error_message = "Invalid Status of the Causal Investigatio. Please select one of the following values: "+(sifStatusVL.toString().replaceAll(",",", "));
      }
    }
    e['invalid_fields']=invalid_fields;
    e['has_validation_warning']=has_validation_warning;
    e['has_validation_error']=has_validation_error;
    e['is_duplicate_error']=is_duplicate_error;
    e['error_message']=error_message;
    console.log(error_message);
    return
  }

  const onCompanySelected = useCallback((e) => {
    fileInputRef.current.value = null;
    setOriginalData(null);
    for (var i=0; i<companiesList.length; i++){
      if (companiesList[i].company_id === e.value) {
        setCurrentCompany(companiesList[i]);
      }
    }
  }, [companiesList])

  const onUploadSuccess = useCallback((data) => {
    notify("Data uploaded succesfully.", 'success', 5000);
    setCurrentCompany(null);
    setOriginalData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    setLoading(false);
    navigate("/sifdata");
  }, [navigate])

  const onUploadData = useCallback((e) => {
    let data = originalData.filter(item => item.has_validation_error===false);
    data = data.filter(item => item.has_validation_warning===false);
    data = data.filter(item => item.is_duplicate_error===false);
    for (var i=0; i<data.length; i++) {
      for (var j=0; j<data[i].invalid_fields.length; j++) {
        data[i][data[i].invalid_fields[j]] = "";
      }
      delete data[i]['invalid_fields'];
      delete data[i]['has_validation_error'];
      delete data[i]['has_validation_warning'];
      delete data[i]['is_duplicate_error'];
      delete data[i]['error_message'];

    }
    if (data.length > 0) {
      updateAPIReportData("/reports/data/sif", user, data, onUploadSuccess, onError)
      setLoading(true);
    } else {
      notify("No data was uploaded.", 'warning', 5000);
      navigate("/sifdata");
    }

  }, [originalData, user, onUploadSuccess, onError])

  const renderGridCell = (data) => {
    //console.log(data);
    return (
    <div className={data.data.invalid_fields.includes(data.column.name)?"text-danger":""}>
      {data.text}
    </div>
  )};

  const renderIStatusCell = (data) => {
    var icon_color = "text-success";
    var icon = "fas fa-check-circle";
    var tttext = null;
    /*if (data.data.has_validation_warning) {
      icon_color = "text-warning";
      icon = "fas fa-exclamation-triangle";
      tttext = "The field marked in red will not be imported. Please fix them."
    }*/
    if (data.data.has_validation_error || data.data.has_validation_warning) {
      icon_color = "text-danger";
      icon = "fas fa-exclamation-circle";
      tttext = "The event will not be imported! Please fix the fields marked in red. "+data.data['error_message'];
    }
    if (data.data.is_duplicate_error) {
      icon_color = "text-danger";
      icon = "fas fa-copy";
      tttext = "The event will not be imported. You can not create multiple events with the same date."
    }
    let ttid = "ttid-"+crypto.randomUUID()


    return (
    <div className={icon_color}>
      <i className={icon} id={ttid}></i>
      {tttext && <Tooltip
        target={"#"+ttid}
        showEvent="mouseenter"
        hideEvent="mouseleave"
        hideOnOutsideClick={false}
      >
        <p style={{width: "300px", textWrap:"wrap"}}>{tttext}</p>
      </Tooltip>}
    </div>
  )};



  if(!loading) {
    return (
      <React.Fragment>

        <div className="input-group">
          <input id="file" type="file"
            ref={fileInputRef} className="dxButton"
            onChange={handleFileChange} accept=".tsv, .txt" hidden/>
        </div>


        <h2 className={'content-block'}>Serious Injury or Fatality Bulk Data Upload</h2>
        <hr className="mt-0 mb-5"></hr>

        <div className={'content-block'}>
          {isAdmin && <div>
          <h5> Choose the company you want to upload the data for</h5>
          <SelectBox
              dataSource={companiesList}
              showDropDownButton={true}
              activeStateEnabled={true}
              searchEnabled={true}
              valueExpr='company_id'
              displayExpr='company_name'
              onValueChanged={onCompanySelected}
          >
          </SelectBox>
          </div>

          }
          <h5 className="mt-3"> 1. Download Template</h5>
          <p>Click the button below to download the excel template file and then populate it with your data.</p>
          <a href={"/SIFTemplate.xlsx"} download="SIFTemplate.xlsx" target='_blank' rel="noreferrer">
            <Button
              text="Download Template"
              stylingMode="outlined"
              type="default"
            />
          </a>

          <h5 className="mt-3"> 2. Copy-Paste your data </h5>
          <p>Copy all your data, including the first row, from the SIFEvents worksheet and then press Paste Data below.</p>
          <Button
            text="Paste Data"
            className="me-2"
            stylingMode="outlined"
            type="default"
            disabled={currentCompany === null}
            onClick={readClipboard}
          />
          {false && <Button
            text="Upload tab separated values file"
            stylingMode="outlined"
            type="default"
            disabled={currentCompany === null}
            onClick={()=>fileInputRef.current.click()}
          />}
        </div>
        {originalData && <div className={'content-block'}>
          <h5>3. Review your data and upload</h5>
          <p>If you have to change the data go back to your excel template, make the necessary changes and repeat step 2. When all your data looks good press Upload Data below.</p>
          <Button
            className="mb-3"
            text="Upload Data"
            type="danger"
            onClick={onUploadData}
          />

          <DataGrid id="dataGrid"
            dataSource={originalData}
            columnHidingEnabled={true}
            cellHintEnabled={true}
            wordWrapEnabled={true}
          >

            <Column
              dataField={'has_validation_error'}
              caption={''}
              width="35px"
              visibleIndex={0}
              cellRender={renderIStatusCell}
            >
            </Column>
            <Column
              dataField={'Company'}
              caption={'Company'}
              visibleIndex={0}
              width="120px"
              hidingPriority={24}
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'DATE OF EVENT (m/d/yyyy)'}
              caption={'Date'}
              dataType="date"
              defaultSortOrder="desc"
              visibleIndex={1}
              hidingPriority={32}
              width="120px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Time of Event'}
              caption={'Time'}
              visibleIndex={2}
              hidingPriority={31}
              width="190px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Area/Basin'}
              caption={'Area/Basin'}
              visibleIndex={3}
              hidingPriority={30}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'State'}
              caption={'State'}
              visibleIndex={4}
              hidingPriority={29}
              width="90px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'County'}
              caption={'County'}
              visibleIndex={5}
              hidingPriority={22}
              width="120px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Type'}
              caption={'Type'}
              visibleIndex={6}
              hidingPriority={28}
              width="90px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Number of Fatalities'}
              caption={'Fatalities'}
              visibleIndex={7}
              hidingPriority={27}
              width="120px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Number of Serious Injuries'}
              caption={'Injuries'}
              visibleIndex={8}
              hidingPriority={26}
              width="110px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'SIF CATEGORY'}
              caption={'Category'}
              visibleIndex={9}
              hidingPriority={25}
              width="220px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'SIF Condition'}
              caption={'Condition'}
              visibleIndex={10}
              hidingPriority={21}
              width="200px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'IOGP Life-Saving Rule Violation'}
              caption={'IOGP Life-Saving Rule Violation'}
              visibleIndex={12}
              hidingPriority={18}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'Discipline of Activity at Time of Event'}
              caption={'Discipline'}
              visibleIndex={13}
              hidingPriority={24}
              width="220px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Activity at Time of Event'}
              caption={'Activity'}
              visibleIndex={14}
              hidingPriority={20}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Activity Subgroup or Task'}
              caption={'Activity Subgroup or Task'}
              visibleIndex={15}
              hidingPriority={19}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'DESCRIPTION OF INCIDENT'}
              caption={'Description of Incident'}
              visibleIndex={16}
              hidingPriority={11}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Was it reasonable that a person was or could have been in proximity at the time of the event?'}
              caption={'Was it reasonable that a person was or could have been in proximity at the time of the event?'}
              visibleIndex={17}
              hidingPriority={14}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'}
              caption={'Did a barrier/control prevent or mitigate the event from becoming an Actual SIF?'}
              visibleIndex={18}
              hidingPriority={13}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Primary Causal Factors'}
              caption={'Primary Causal Factors'}
              visibleIndex={19}
              hidingPriority={16}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Column
              dataField={'Cause Description'}
              caption={'Cause Description'}
              visibleIndex={20}
              hidingPriority={15}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>
            <Column
              dataField={'What is the status of the Causal Investigation?'}
              caption={'Investigation Status'}
              visibleIndex={21}
              hidingPriority={12}
              width="150px"
              cellRender={renderGridCell}
            >
            </Column>

            <Paging defaultPageSize={100} />
          </DataGrid>
        </div>}
      </React.Fragment>
    )
  } else {
    return (
      <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
          <LoadIndicator
              id="large-indicator"
              height={60}
              width={60}
          />
      </div>
    )
  }

}
